import { z } from "zod"

const RetryConfigSchema = z.object({
  retries: z.number().int().min(0).optional(),
  delayFactor: z.number().positive().optional(),
  maxDelay: z.number().positive().optional(),
})

export type RetryConfig = z.infer<typeof RetryConfigSchema>

function shouldRetryRequest(error: unknown, method: string): boolean {
  const errorObj = error as {
    name?: string
    code?: string
    response?: { status: number }
  }

  if (errorObj.name === "AbortError" || errorObj.code === "ECONNABORTED") {
    return false
  }

  if (!errorObj.response) {
    return true
  }

  const status = errorObj.response.status
  const isIdempotentMethod = [
    "GET",
    "HEAD",
    "OPTIONS",
    "PUT",
    "DELETE",
  ].includes(method.toUpperCase())

  // axios-utils specific: only retry 501-599 status codes on idempotent methods
  return isIdempotentMethod && status >= 501 && status <= 599
}

function isRetryConfig(obj: unknown): obj is RetryConfig {
  if (typeof obj !== "object" || obj === null) {
    return false
  }

  const hasRetryConfigProp =
    "retries" in obj || "delayFactor" in obj || "maxDelay" in obj
  if (!hasRetryConfigProp) {
    return false
  }

  const hasRequestInitProp =
    "method" in obj ||
    "headers" in obj ||
    "body" in obj ||
    "signal" in obj ||
    "cache" in obj
  if (hasRequestInitProp) {
    return false
  }

  return RetryConfigSchema.safeParse(obj).success
}

export async function fetchWithRetry(
  fetchFn: typeof fetch,
  ...args:
    | [input: Parameters<typeof fetch>[0]]
    | [input: Parameters<typeof fetch>[0], config: RetryConfig]
    | [input: Parameters<typeof fetch>[0], init: Parameters<typeof fetch>[1]]
    | [
        input: Parameters<typeof fetch>[0],
        init: Parameters<typeof fetch>[1],
        config: RetryConfig,
      ]
): Promise<Response> {
  const lastArg = args[args.length - 1]
  const hasConfig = isRetryConfig(lastArg)

  const fetchArgs = hasConfig
    ? (args.slice(0, -1) as Parameters<typeof fetch>)
    : (args as Parameters<typeof fetch>)
  const config = hasConfig ? lastArg : {}

  const { retries = 3, delayFactor = 100, maxDelay = 30000 } = config
  let lastError: Error | undefined

  const [, options] = fetchArgs
  const method = options?.method || "GET"

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const response = await fetchFn(...fetchArgs)

      if (!response.ok) {
        const shouldRetry = shouldRetryRequest(
          { response: { status: response.status } },
          method
        )

        if (!shouldRetry || attempt === retries) {
          return response
        }
      } else {
        return response
      }
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error))
      const shouldRetry = shouldRetryRequest(error, method)

      if (!shouldRetry || attempt === retries) {
        throw lastError
      }
    }

    const delay = Math.min(delayFactor * 2 ** attempt, maxDelay)
    await new Promise((resolve) => setTimeout(resolve, delay))
  }

  throw lastError || new Error("Unexpected retry loop exit")
}
